import { USAGE_ACTION } from '../constants/sample.constants';
import {
  findFirstQuantityExceedance,
  getRemainingBeforeRecord,
  resolveInitialQuantityFromRecords,
} from './sampleUsageRecordCalc';

export type UsageRecordEditRow = {
  usageDate: string;
  action: string;
  purpose?: string;
  quantity: number;
  remainingQuantity: number;
  recordedBy?: string;
  clientKey: string;
};

export type ApplyUsageRecordFieldChangeArgs<T extends UsageRecordEditRow> = {
  records: T[];
  index: number;
  field: string;
  value: unknown;
  sampleCreatedDate: string;
  initialQuantityFallback: number | null | undefined;
  /** 編集対象サンプルがあるときのみ数量超過チェック */
  enforceQuantityAgainstInitial: boolean;
  rowKey: (row: T) => string;
  recalculate: (rows: T[]) => T[];
  messages: {
    usageDateBeforeCreated: string;
    quantityExceeds: (remaining: number) => string;
    quantityExceedsAfterRecalc: (quantity: number, remaining: number) => string;
  };
};

export type ApplyUsageRecordFieldChangeResult<T extends UsageRecordEditRow> =
  | { ok: true; records: T[]; initialQuantity?: number }
  | { ok: false; error: string };

/**
 * 編集モーダル内の使用履歴1フィールド更新（バリデーション + 残量再計算）。
 */
export function applyUsageRecordFieldChange<T extends UsageRecordEditRow>(
  args: ApplyUsageRecordFieldChangeArgs<T>
): ApplyUsageRecordFieldChangeResult<T> {
  const {
    records,
    index,
    field,
    value,
    sampleCreatedDate,
    initialQuantityFallback,
    enforceQuantityAgainstInitial,
    rowKey,
    recalculate,
    messages,
  } = args;

  const updated = [...records];
  updated[index] = { ...updated[index], [field]: value };

  if (
    field === 'usageDate' &&
    sampleCreatedDate &&
    typeof value === 'string' &&
    value.trim() < sampleCreatedDate.trim()
  ) {
    const currentAction = updated[index].action;
    if (currentAction !== USAGE_ACTION.CREATED) {
      return { ok: false, error: messages.usageDateBeforeCreated };
    }
  }

  if (
    field === 'quantity' &&
    updated[index].action !== USAGE_ACTION.CREATED &&
    enforceQuantityAgainstInitial
  ) {
    const quantityValue =
      typeof value === 'number' ? value : parseInt(String(value), 10) || 0;
    const initialQuantity = resolveInitialQuantityFromRecords(
      updated,
      initialQuantityFallback ?? 0
    );
    const runningRemaining = getRemainingBeforeRecord(
      updated,
      rowKey(updated[index]),
      initialQuantity,
      rowKey
    );
    if (quantityValue > runningRemaining) {
      return { ok: false, error: messages.quantityExceeds(runningRemaining) };
    }
  }

  let initialQuantityUpdate: number | undefined;
  if (field === 'remainingQuantity' && updated[index].action === USAGE_ACTION.CREATED) {
    initialQuantityUpdate =
      typeof value === 'number' ? value : parseInt(String(value), 10) || 0;
  }

  const recalculated = recalculate(updated);

  if (enforceQuantityAgainstInitial) {
    const initialQuantity = resolveInitialQuantityFromRecords(
      recalculated,
      initialQuantityFallback ?? 0
    );
    const exceedance = findFirstQuantityExceedance(recalculated, initialQuantity);
    if (exceedance) {
      return {
        ok: false,
        error: messages.quantityExceedsAfterRecalc(
          exceedance.quantity,
          exceedance.remaining
        ),
      };
    }
  }

  return {
    ok: true,
    records: recalculated,
    ...(initialQuantityUpdate !== undefined
      ? { initialQuantity: initialQuantityUpdate }
      : {}),
  };
}

/** 送信前: 作製日より前の使用日がないか */
export function findUsageDateBeforeCreated(
  records: Array<{ action?: string; usageDate?: string }>,
  sampleCreatedDate: string
): boolean {
  if (!sampleCreatedDate) return false;
  const created = sampleCreatedDate.trim();
  for (const record of records) {
    if (record.action?.trim() === USAGE_ACTION.CREATED) continue;
    if (record.usageDate && record.usageDate.trim() < created) return true;
  }
  return false;
}
